import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Inbox, ArrowLeft, Moon, Sun } from "lucide-react";
import { useTheme } from "../context/ThemeContext";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { LEGAL_TEXTS, LEGAL_SLUGS } from "../legal/legalTexts";

const EMAIL_RE = /([\w.+-]+@[\w-]+\.[\w.]+)/g;

function RichText({ text }) {
  // Les adresses email contenues dans les textes juridiques deviennent des liens mailto
  const parts = text.split(EMAIL_RE);
  return (
    <>
      {parts.map((part, i) =>
        EMAIL_RE.test(part) && part.includes("@") ? (
          <a key={i} href={`mailto:${part}`} className="text-electric font-medium hover:underline">{part}</a>
        ) : (
          <React.Fragment key={i}>{part}</React.Fragment>
        )
      )}
    </>
  );
}

function Block({ b }) {
  if (b.list) {
    return (
      <ul className="my-3 space-y-1.5 pl-5 list-disc marker:text-electric/60 text-[15px] leading-relaxed text-foreground/85">
        {b.list.map((item, i) => <li key={i}><RichText text={item} /></li>)}
      </ul>
    );
  }
  if (b.block) {
    return (
      <div className="my-3 rounded-xl border border-border bg-secondary/40 px-5 py-4 text-[15px] leading-relaxed text-foreground/85">
        {b.block.map((line, i) => <p key={i}><RichText text={line} /></p>)}
      </div>
    );
  }
  return <p className="my-3 text-[15px] leading-relaxed text-foreground/85"><RichText text={b.p} /></p>;
}

export default function LegalDoc({ slug }) {
  const { t, i18n } = useTranslation();
  const { dark, toggle } = useTheme();
  const doc = LEGAL_TEXTS[slug];
  const isFr = (i18n.language || "fr").startsWith("fr");

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!doc) return null;

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col" data-testid={`legal-${slug}`}>
      <header className="fixed top-0 inset-x-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="legal-logo">
            <div className="h-8 w-8 rounded-lg bg-primary dark:bg-electric flex items-center justify-center">
              <Inbox size={18} className="text-white" />
            </div>
            <span className="font-display font-bold text-lg tracking-tight">{t("common.appName")}</span>
          </Link>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <button onClick={toggle} data-testid="legal-theme-toggle" className="p-2 rounded-full hover:bg-secondary transition-colors">
              {dark ? <Sun size={17} /> : <Moon size={17} />}
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1 w-full max-w-3xl mx-auto px-6 pt-32 pb-20">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}>
          <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter" data-testid="legal-title">
            {doc.title}
          </h1>
          <p className="mt-3 text-xs uppercase tracking-[0.2em] text-muted-foreground" data-testid="legal-updated">
            {t("legal.updated")} : {doc.updated}
          </p>
          {!isFr && (
            <p className="mt-4 rounded-xl border border-border bg-secondary/40 px-4 py-3 text-xs text-muted-foreground leading-relaxed" data-testid="legal-fr-reference">
              {t("legal.frReference")}
            </p>
          )}
        </motion.div>

        {doc.sections.length > 4 && (
          <nav className="mt-10 rounded-2xl border border-border bg-card p-6" data-testid="legal-toc">
            <p className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-4">{t("legal.toc")}</p>
            <ol className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5">
              {doc.sections.map((s, i) => (
                <li key={i}>
                  <a href={`#legal-section-${i}`}
                    className="text-sm text-foreground/70 hover:text-electric transition-colors">
                    {s.h}
                  </a>
                </li>
              ))}
            </ol>
          </nav>
        )}

        <article className="mt-12 space-y-10" data-testid="legal-content">
          {doc.sections.map((s, i) => (
            <section key={i} id={`legal-section-${i}`} className="scroll-mt-24" data-testid={`legal-section-${i}`}>
              <h2 className="font-display font-bold text-lg sm:text-xl tracking-tight mb-2">{s.h}</h2>
              {s.blocks.map((b, j) => <Block key={j} b={b} />)}
            </section>
          ))}
        </article>

        <div className="mt-16 pt-8 border-t border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <Link to="/" data-testid="legal-back"
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft size={15} /> {t("contact.back")}
          </Link>
          <nav className="flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-muted-foreground" data-testid="legal-nav">
            {LEGAL_SLUGS.filter((s) => s !== slug).map((s) => (
              <Link key={s} to={`/${s}`} className="hover:text-foreground transition-colors">
                {t(`legal.docNames.${s}`)}
              </Link>
            ))}
          </nav>
        </div>
      </main>
    </div>
  );
}
